import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ArrowLeft, Newspaper, Calendar, Share2, ExternalLink, Lightbulb, Globe, Flag, CarFront, Sparkles, Battery, Cpu, Factory, BookOpen, ShieldCheck, Leaf, FileText, TrendingUp } from "lucide-react";
import ReactMarkdown from "react-markdown";
import SEOHead from "@/components/SEOHead";
import { schemaNewsArticle, schemaBreadcrumb } from "@/lib/siteSchemas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RUBRICA_LABEL = {
  noleggio:     "Noleggio & Flotte",
  novita_motor: "Novità Modelli",
  elettrico:    "Elettrico & Ibrido",
  tecnologia:   "Tecnologia & ADAS",
  industria:    "Industria & Mercato",
  guide:        "Guide & Consigli",
  sicurezza:    "Sicurezza Stradale",
  green:        "Mobilità Sostenibile",
};
const RUBRICA_ICON = {
  noleggio:     CarFront,
  novita_motor: Sparkles,
  elettrico:    Battery,
  tecnologia:   Cpu,
  industria:    Factory,
  guide:        BookOpen,
  sicurezza:    ShieldCheck,
  green:        Leaf,
};

export default function NovitaNoleggioDetailPage() {
  const { slug } = useParams();
  const [art, setArt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/news-noleggio/${slug}`).then(r => setArt(r.data))
      .catch(() => setArt(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (<><Header /><div className="min-h-screen flex items-center justify-center pt-20"><div className="w-10 h-10 border-2 border-[#163321] border-t-transparent rounded-full animate-spin" /></div></>);

  if (!art) return (
    <><Header />
    <main className="min-h-screen bg-[#FCFCFA] pt-24">
      <div className="max-w-2xl mx-auto px-5 py-20 text-center">
        <Newspaper className="w-12 h-12 text-[#4A5E51]/30 mx-auto mb-3" />
        <p className="text-sm text-[#4A5E51] mb-4">Articolo non trovato o non più disponibile.</p>
        <Link to="/noleggio/novita" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#163321] hover:text-[#D97B58] transition-colors">
          <ArrowLeft className="w-4 h-4" /> Torna alle rubriche
        </Link>
      </div>
    </main><Footer /></>
  );

  const date = art.published_at ? new Date(art.published_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }) : "";
  const rubLabel = RUBRICA_LABEL[art.rubrica] || "Novità";
  const RubIcon = RUBRICA_ICON[art.rubrica] || Sparkles;
  const isItalian = art.fonte_nome && !/(UK|US|FR|DE)/i.test(art.fonte_nome);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: art.titolo, url }); } catch { /* cancel */ }
    } else {
      try { await navigator.clipboard.writeText(url); alert("Link copiato"); } catch { /* no-op */ }
    }
  };

  return (
    <>
      <SEOHead
        title={`${art.titolo} | Redazione Colucci`}
        description={(art.sommario || "").slice(0, 160) || `Articolo della redazione Colucci su ${rubLabel}.`}
        canonical={`/noleggio/novita/${art.slug}`}
        ogImage={art.immagine_url || "https://www.colucciassicuratori.com/og-image.png"}
        ogType="article"
        keywords={(art.tag || "").split(",").map((t) => t.trim()).filter(Boolean)}
        articleMeta={{
          publishedTime: art.published_at,
          modifiedTime: art.published_at,
          author: "Redazione Colucci",
          section: rubLabel,
        }}
        jsonLd={[
          schemaNewsArticle({
            headline: art.titolo,
            description: (art.sommario || "").slice(0, 200),
            datePublished: art.published_at,
            image: art.immagine_url,
            slug: art.slug,
            sectionName: rubLabel,
          }),
          schemaBreadcrumb([
            { name: "Home", path: "/" },
            { name: "Noleggio", path: "/noleggio" },
            { name: "Novità", path: "/noleggio/novita" },
            { name: art.titolo, path: `/noleggio/novita/${art.slug}` },
          ]),
        ]}
      />
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="novita-detail-page">
        <article className="max-w-3xl mx-auto px-5 sm:px-6 py-8 sm:py-12">
          <Link to="/noleggio/novita" className="inline-flex items-center gap-1.5 text-xs text-[#4A5E51] hover:text-[#163321] mb-6 transition-colors" data-testid="back-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Tutte le novità
          </Link>

          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#D97B58]/10 text-[#D97B58] border border-[#D97B58]/20">
              <RubIcon className="w-3 h-3" /> {rubLabel}
            </span>
            {art.rilevanza === "alta" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#163321] text-white">
                <TrendingUp className="w-3 h-3" /> Alta rilevanza
              </span>
            )}
            {date && <span className="text-[11px] text-[#4A5E51] flex items-center gap-1"><Calendar className="w-3 h-3" />{date}</span>}
            {art.tag && <span className="text-[11px] text-[#4A5E51]">· {art.tag}</span>}
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F1F14] leading-tight mb-4" data-testid="articolo-titolo">{art.titolo}</h1>
          {art.sommario && <p className="text-lg text-[#4A5E51] leading-relaxed mb-6" data-testid="articolo-sommario">{art.sommario}</p>}

          {art.immagine_url && (
            <div className="aspect-[16/9] bg-zinc-100 rounded-2xl overflow-hidden mb-8">
              <img src={art.immagine_url} alt={art.titolo} className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
            </div>
          )}

          {/* Contenuto principale */}
          <div className="prose prose-zinc max-w-none prose-headings:font-heading prose-headings:text-[#163321] prose-a:text-[#D97B58] prose-strong:text-[#0F1F14]" data-testid="articolo-contenuto">
            <ReactMarkdown>{art.contenuto_markdown || art.contenuto || ""}</ReactMarkdown>
          </div>

          {/* Fonti citate nell'analisi */}
          {Array.isArray(art.fonti_dati) && art.fonti_dati.length > 0 && (
            <div className="mt-8 bg-[#F5F4F0] border border-[#E5E2DB] rounded-2xl p-5" data-testid="fonti-citate">
              <p className="text-[10px] uppercase tracking-widest font-bold text-[#4A5E51] mb-3 flex items-center gap-1.5">
                <FileText className="w-3 h-3" /> Fonti citate nell'analisi
              </p>
              <ul className="space-y-1.5">
                {art.fonti_dati.map((f, idx) => (
                  <li key={idx} className="text-xs text-[#4A5E51] flex items-start gap-2">
                    <span className="text-[#163321] mt-0.5">•</span>
                    <span className="italic">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Fonte */}
          {art.fonte_nome && (
            <div className="mt-6 pt-4 border-t border-zinc-200 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-xs text-[#4A5E51]">
                {isItalian ? <Flag className="w-3.5 h-3.5" /> : <Globe className="w-3.5 h-3.5" />}
                Fonte originale: <strong className="text-[#0F1F14]">{art.fonte_nome}</strong>
              </div>
              {art.fonte_link && (
                <a href={art.fonte_link} target="_blank" rel="noopener noreferrer nofollow"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[#D97B58] hover:text-[#163321] transition-colors" data-testid="fonte-link">
                  Leggi articolo originale <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          )}

          {/* Commento Colucci */}
          {art.commento_colucci && (
            <div className="mt-8 bg-gradient-to-br from-[#0F1F14] to-[#1d4a2e] text-white rounded-2xl p-6 relative overflow-hidden" data-testid="commento-colucci">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#D97B58]/10 blur-3xl" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-3">
                  <img src="/colucci_solutions_web.png" alt="Colucci Solutions" className="h-7 object-contain brightness-0 invert" />
                  <span className="text-[10px] uppercase tracking-widest font-bold text-[#D97B58]">Il commento</span>
                </div>
                <p className="text-sm leading-relaxed text-white/90">{art.commento_colucci}</p>
              </div>
            </div>
          )}

          {/* Suggerimento */}
          {art.suggerimento && (
            <div className="mt-5 bg-[#D97B58]/10 border border-[#D97B58]/30 rounded-2xl p-5 flex gap-4" data-testid="suggerimento">
              <div className="w-10 h-10 shrink-0 rounded-full bg-[#D97B58] text-white flex items-center justify-center">
                <Lightbulb className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-widest font-bold text-[#D97B58] mb-1">Il nostro suggerimento</p>
                <p className="text-sm text-[#0F1F14] leading-relaxed">{art.suggerimento}</p>
              </div>
            </div>
          )}

          {/* CTA + Share */}
          <div className="flex items-center justify-between mt-10 pt-6 border-t border-zinc-200 flex-wrap gap-3">
            <button onClick={share} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#163321] text-white text-xs font-semibold hover:bg-[#1d4a2e] transition-colors" data-testid="share-btn">
              <Share2 className="w-3.5 h-3.5" /> Condividi
            </button>
            <Link to="/noleggio" className="inline-flex items-center gap-1 text-xs font-semibold text-[#D97B58] hover:text-[#163321] transition-colors">
              <CarFront className="w-3.5 h-3.5" /> Scopri il catalogo NLT →
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}
